import type { AlertCriteria, SavedSearch } from './userApi';

/** A pre-populated alert draft for the "Convert to alert" flow. */
export interface AlertDraft {
  name: string;
  criteria: AlertCriteria;
}

function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((v) => String(v)).filter((v) => v !== '');
  if (value === null || value === undefined || value === '') return [];
  return [String(value)];
}

function toNumber(value: unknown): number | undefined {
  const raw = Array.isArray(value) ? value[0] : value;
  if (raw === null || raw === undefined || raw === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

function toNumberArray(value: unknown): number[] {
  return toStringArray(value)
    .map((s) => Number(s))
    .filter((n) => Number.isFinite(n));
}

function isTruthy(value: unknown): boolean {
  return value === true || value === 'true' || value === '1' || value === 1;
}

/**
 * Map a saved search into an {@link AlertDraft} for the "Convert to alert"
 * flow (#199). Search filter keys overlap with but don't match AlertCriteria
 * 1:1, so this translates the ones the alert form supports:
 *
 *   caliber, brand, bulletType, bulletWeightGrains → same-named criteria
 *   maxPricePerRound, minRoundCount               → same-named criteria
 *   excludeRemanufactured (search) → excludeReloaded (alert)
 *   lotNumber (search, singular)   → lotNumbers (alert, array)
 *
 * The free-text `q` and the min-price filter have no alert equivalent and are
 * dropped. An alert-backed saved search that already carries `criteria` seeds
 * from it first. The user reviews the pre-filled dialog before saving, so an
 * empty result just surfaces the form's "set at least one criterion" prompt.
 */
export function savedSearchToAlertDraft(savedSearch: SavedSearch): AlertDraft {
  const filters = (savedSearch.filters ?? {}) as Record<string, unknown>;
  const criteria: AlertCriteria = savedSearch.criteria ? { ...savedSearch.criteria } : {};

  const caliber = toStringArray(filters.caliber);
  if (caliber.length) criteria.caliber = caliber;

  const brand = toStringArray(filters.brand);
  if (brand.length) criteria.brand = brand;

  const bulletType = toStringArray(filters.bulletType);
  if (bulletType.length) criteria.bulletType = bulletType;

  const bulletWeightGrains = toNumberArray(filters.bulletWeightGrains);
  if (bulletWeightGrains.length) criteria.bulletWeightGrains = bulletWeightGrains;

  const maxPricePerRound = toNumber(filters.maxPricePerRound);
  if (maxPricePerRound !== undefined) criteria.maxPricePerRound = maxPricePerRound;

  const minRoundCount = toNumber(filters.minRoundCount);
  if (minRoundCount !== undefined) criteria.minRoundCount = minRoundCount;

  if (isTruthy(filters.excludeRemanufactured)) criteria.excludeReloaded = true;

  const lotNumbers = toStringArray(filters.lotNumber);
  if (lotNumbers.length) criteria.lotNumbers = lotNumbers;

  return { name: savedSearch.name, criteria };
}
